import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TokenService {

  private isRenewing = false;
  private renewComplete$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  getAccessToken(): string | null {
    return localStorage.getItem('userAccessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('userRefreshToken');
  }

  /**
   * Decode JWT payload without a library.
   * Returns null if the token is malformed.
   */
  decodeToken(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Check whether the access token exists and is not expired.
   * Treats a token as expired if it expires within `bufferSeconds` (default 60s).
   */
  isTokenValid(bufferSeconds: number = 60): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return false;

    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp > nowSeconds + bufferSeconds;
  }

  /**
   * Returns the number of seconds until the access token expires.
   * Returns 0 if missing / already expired.
   */
  secondsUntilExpiry(): number {
    const token = this.getAccessToken();
    if (!token) return 0;

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return 0;

    const remaining = payload.exp - Math.floor(Date.now() / 1000);
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Single renewal entry-point.
   * - Deduplicates: if a renewal is already in-flight, waits for it.
   * - On success: updates BOTH `userAccessToken` and the `userProfile` object.
   * - On 401: clears storage and redirects to /login.
   */
  renewToken(): Observable<any> {
    if (this.isRenewing) {
      return this.renewComplete$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => of(true)),
      );
    }

    this.isRenewing = true;
    this.renewComplete$.next(false);

    const email = localStorage.getItem('userEmail');
    const refreshToken = this.getRefreshToken();

    if (!email || !refreshToken) {
      this.isRenewing = false;
      this.clearAndRedirect();
      return throwError(() => new Error('Missing credentials for token renewal'));
    }

    const href = environment.AUTH_API_URL + 'aws-auth/renewToken';
    const body = {
      Username: email,
      Refreshtoken: refreshToken,
    };

    return this.http.post<any>(href, body, {
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      tap(res => {
        if (res) {
          // Update individual localStorage keys (used by main HttpService)
          if (res.idToken) {
            localStorage.setItem('userAccessToken', res.idToken);
          }
          if (res.refreshToken) {
            localStorage.setItem('userRefreshToken', res.refreshToken);
          }

          // Also update the userProfile object (used by sembra-components HttpService)
          this.syncUserProfile(res);
        }

        this.isRenewing = false;
        this.renewComplete$.next(true);
      }),
      catchError(error => {
        this.isRenewing = false;
        this.renewComplete$.next(true);

        if (error.status === 401) {
          this.clearAndRedirect();
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Keep the `userProfile` JSON blob in sync with the individual keys.
   */
  private syncUserProfile(res: any): void {
    const raw = localStorage.getItem('userProfile');
    if (!raw) return;

    try {
      const profile = JSON.parse(raw);
      if (res.idToken) {
        profile.idToken = res.idToken;
        profile.accessToken = res.idToken;
      }
      if (res.refreshToken) {
        profile.refreshToken = res.refreshToken;
      }
      localStorage.setItem('userProfile', JSON.stringify(profile));
    } catch {
      // userProfile wasn't valid JSON — nothing to sync
    }
  }

  clearAndRedirect(): void {
    const currentUrl = this.router.url;
    const redirectUrl = currentUrl.indexOf('/threads/') > -1 ? '/threads' : currentUrl;

    localStorage.clear();
    localStorage.setItem('redirectUrl', redirectUrl);
    this.router.navigate(['/login']);
  }
}
